import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import type { LiveCheckinMonthTask, LiveCheckinTaskDefault } from '../types/check-in'
import { formatNumber } from '../helpers/format'
import { taskFallback } from '../helpers/task-labels'

interface Props {
  task: LiveCheckinMonthTask | null
  def?: LiveCheckinTaskDefault | null
}

/**
 * Card grande de uma tarefa (versão expandida pro carrossel — estilo
 * rocket-game-modal: emoji grande no centro, status, barra de progresso).
 */
export const TaskCard = memo(({ task, def }: Props) => {
  const { t } = useTranslation()
  const fallback = taskFallback(task?.liveCheckinTaskType ?? 'check_in')
  const name = def?.nameKey
    ? t(def.nameKey, { defaultValue: def.internalName ?? fallback.name })
    : (def?.internalName ?? fallback.name)
  const description = def?.descriptionKey
    ? t(def.descriptionKey, { defaultValue: def.internalDescription ?? '' })
    : (def?.internalDescription ?? null)
  const emoji = def?.emoji ?? fallback.emoji

  const target = task?.targetValue || def?.targetValue || 1
  const done = task ? Math.min(task.quantityCompleted ?? 0, target) : 0
  const pct = Math.min(100, (done / target) * 100)
  const isCompleted = !!task?.completed
  const pointReward = task?.pointReward ?? def?.pointReward ?? 0

  const statusStyle = isCompleted
    ? {
        bg: 'from-hug-mint/20 to-hug-mint/10 border-hug-mint/40',
        bar: 'from-hug-mint/80 to-hug-mint',
        chip: 'bg-hug-mint/80',
        label: 'Concluída',
      }
    : pct > 0
      ? {
          bg: 'from-hug-purple/25 to-hug-lilac/15 border-hug-purple/50',
          bar: 'from-hug-purple to-hug-lilac',
          chip: 'bg-hug-purple shadow-[0_0_10px_var(--hug-glow)]',
          label: 'Em progresso',
        }
      : {
          bg: 'from-white/[0.08] to-white/[0.04] border-white/10',
          bar: 'from-hug-purple to-hug-lilac',
          chip: 'bg-white/10',
          label: 'Não iniciada',
        }

  return (
    <div className="w-full">
      {/* chip status */}
      <div className="mb-2 flex justify-center">
        <span
          className={`rounded-full px-3 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-white ${statusStyle.chip}`}
        >
          {statusStyle.label}
        </span>
      </div>

      {/* emoji central com glow */}
      <div className="relative mx-auto flex h-32 w-32 items-center justify-center">
        <div
          aria-hidden
          className="absolute inset-0 rounded-full"
          style={{
            background:
              'radial-gradient(circle, var(--hug-glow) 0%, rgba(155,48,230,0) 70%)',
          }}
        />
        <span className="relative text-7xl drop-shadow-[0_4px_20px_var(--hug-glow-soft)]">
          {emoji}
        </span>
      </div>

      {/* nome + descrição */}
      <div className="mt-3 text-center">
        <h2 className="text-lg font-extrabold text-white">{name}</h2>
        {description && (
          <p className="mt-0.5 text-xs text-white/70">{description}</p>
        )}
      </div>

      {/* card de progresso */}
      <div
        className={`mt-4 w-full rounded-2xl border bg-gradient-to-br p-4 backdrop-blur-md ${statusStyle.bg}`}
      >
        <div className="flex items-center justify-between text-sm font-semibold">
          <span>Progresso</span>
          <span className="rounded-full bg-hug-gold/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-hug-gold">
            +{formatNumber(pointReward)} pts
          </span>
        </div>

        <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-black/30">
          <div
            className={`h-full rounded-full bg-gradient-to-r transition-[width] duration-700 ease-out ${statusStyle.bar}`}
            style={{ width: `${pct}%` }}
          />
        </div>

        <div className="mt-1.5 flex items-center justify-between text-[11px] text-white/80">
          <span>{formatNumber(done)} de {formatNumber(target)}</span>
          <span>{Math.round(pct)}%</span>
        </div>
      </div>
    </div>
  )
})

TaskCard.displayName = 'TaskCard'
